import { utcCalendarDate } from '@infra/format';
import { HTTP_STATUS } from '@infra/http';
import { fetchAllPages, type PagedResponse } from '@shared/constants';
import { apiRequest, coreHttp } from '../../http';
import { BOOKING_LIMITS, SLOT_PAGE_LIMIT } from './booking.limits';
import { syncServerClock } from './server-clock';
import type {
  AdminSlot,
  BookingPolicy,
  BookingPlacement,
  BookingPolicyPatch,
  BookingResult,
  BookingSlot,
  BookingUsage,
  DurationSchedule,
  MyBooking,
  OrgScoped,
  PageParams,
  PracticeAccess,
  Reminder,
  SlotBooking,
  SlotRange,
} from './booking.types';

const ROLEPLAY_SESSIONS_ENDPOINT = '/v1/roleplay-sessions';

/** Asks Core whether the session may keep running now. */
export function bookingContinue(sessionUuid: string, signal?: AbortSignal) {
  return apiRequest<PracticeAccess>(
    'post',
    `${ROLEPLAY_SESSIONS_ENDPOINT}/${sessionUuid}/booking-continue`,
    {},
    { signal }
  );
}

const BOOKING_ENDPOINT = '/v1/booking';
const ADMIN_ENDPOINT = `${BOOKING_ENDPOINT}/admin`;

const MS_PER_DAY = 86_400_000;
/** A day that repeats an hour (DST fold) is this much longer than 24 hours. */
const DST_EXTRA_DAY_MINUTES = 60;
/** The shortest slot fills a day with this many slots, the most one day can hold. */
const DAY_SLOT_CAP = Math.ceil(
  (BOOKING_LIMITS.durationMinutes.max + DST_EXTRA_DAY_MINUTES) / BOOKING_LIMITS.durationMinutes.min
);
const DAY_FETCH_OPTIONS = {
  pageSize: SLOT_PAGE_LIMIT,
  maxPages: Math.ceil(DAY_SLOT_CAP / SLOT_PAGE_LIMIT),
  maxItems: DAY_SLOT_CAP,
};
/** Cancelled and released bookings stay listed, so the ceiling is a multiple of the capacity. */
const SLOT_BOOKINGS_MAX = BOOKING_LIMITS.capacity.max * 5;
const SLOT_BOOKINGS_FETCH_OPTIONS = {
  pageSize: SLOT_PAGE_LIMIT,
  maxPages: Math.ceil(SLOT_BOOKINGS_MAX / SLOT_PAGE_LIMIT),
  maxItems: SLOT_BOOKINGS_MAX,
};

/** Every local date of an inclusive range. */
export function localDatesOf(range: SlotRange): string[] {
  const out: string[] = [];
  const end = Date.parse(`${range.toDate}T00:00:00Z`);
  for (let ms = Date.parse(`${range.fromDate}T00:00:00Z`); ms <= end; ms += MS_PER_DAY) {
    out.push(utcCalendarDate(new Date(ms)));
  }
  return out;
}

/** Also reads the response's `Date` so slot states follow the server's clock. */
export async function listSlots(range: SlotRange, paging: PageParams = {}, signal?: AbortSignal) {
  const response = await coreHttp.get<PagedResponse<BookingSlot>>(`${BOOKING_ENDPOINT}/slots`, {
    params: { ...range, ...paging },
    signal,
  });
  syncServerClock(response.headers?.date);
  return response.data;
}

/** A week of slots, one day per request so no single request has to page through the week. */
export async function listWeekSlots(range: SlotRange, signal?: AbortSignal) {
  const days = await Promise.all(
    localDatesOf(range).map((day) =>
      fetchAllPages<BookingSlot>(
        (page, limit, abort) => listSlots({ fromDate: day, toDate: day }, { page, limit }, abort),
        { ...DAY_FETCH_OPTIONS, signal }
      )
    )
  );
  return days.flat();
}

export function listMyBookings(paging: PageParams = {}, signal?: AbortSignal) {
  return apiRequest<PagedResponse<MyBooking>>('get', `${BOOKING_ENDPOINT}/my-bookings`, undefined, {
    params: paging,
    signal,
  });
}

export function getBookingUsage(localDate: string, signal?: AbortSignal) {
  return apiRequest<BookingUsage>('get', `${BOOKING_ENDPOINT}/usage`, undefined, {
    params: { localDate },
    signal,
  });
}

/** Core answers 201 for a new booking and 200 when the seat was already held. */
async function postPlacement(url: string, body: unknown): Promise<BookingPlacement> {
  const response = await coreHttp.post<BookingResult>(url, body);
  return { booking: response.data, created: response.status === HTTP_STATUS.CREATED };
}

/** A slot is named by its start instant (ISO-8601 UTC). */
export function bookSlot(slotStartAt: string) {
  return postPlacement(`${BOOKING_ENDPOINT}/bookings`, { slotStartAt });
}

export function cancelBooking(bookingUuid: string) {
  return apiRequest<BookingResult>('post', `${BOOKING_ENDPOINT}/bookings/${bookingUuid}/cancel`);
}

/** Also reads the response's `Date`, so a page opened from the email follows the server clock. */
export async function readReminder(reminderUuid: string, signal?: AbortSignal) {
  const response = await coreHttp.get<Reminder>(`${BOOKING_ENDPOINT}/reminders/${reminderUuid}`, {
    signal,
  });
  syncServerClock(response.headers?.date);
  return response.data;
}

export function respondToReminder(reminderUuid: string, action: 'keep' | 'release') {
  return apiRequest<Reminder>('post', `${BOOKING_ENDPOINT}/reminders/${reminderUuid}/respond`, {
    action,
  });
}

export function getBookingPolicy(scope: OrgScoped, signal?: AbortSignal) {
  return apiRequest<BookingPolicy>('get', `${ADMIN_ENDPOINT}/policy`, undefined, {
    params: scope,
    signal,
  });
}

export function updateBookingPolicy(scope: OrgScoped, patch: BookingPolicyPatch) {
  return apiRequest<BookingPolicy>('patch', `${ADMIN_ENDPOINT}/policy`, { ...scope, ...patch });
}

/** The only way to flip booking on or off; Core checks every precondition. */
export function setBookingEnabled(scope: OrgScoped, enabled: boolean, expectedVersion: number) {
  return apiRequest<BookingPolicy>('put', `${ADMIN_ENDPOINT}/enabled`, {
    ...scope,
    enabled,
    expectedVersion,
  });
}

export function changeSlotDuration(
  scope: OrgScoped,
  data: { effectiveLocalDate: string; durationMinutes: number }
) {
  return apiRequest<void>('put', `${ADMIN_ENDPOINT}/duration`, { ...scope, ...data });
}

/** The slot length today and every change scheduled after it. */
export function getDurationSchedule(scope: OrgScoped, signal?: AbortSignal) {
  return apiRequest<DurationSchedule>('get', `${ADMIN_ENDPOINT}/duration`, undefined, {
    params: scope,
    signal,
  });
}

export function changeBookingTimezone(scope: OrgScoped, timezone: string) {
  return apiRequest<void>('put', `${ADMIN_ENDPOINT}/timezone`, { ...scope, timezone });
}

export function listAdminSlots(
  scope: OrgScoped,
  range: SlotRange,
  paging: PageParams = {},
  signal?: AbortSignal
) {
  return apiRequest<PagedResponse<AdminSlot>>('get', `${ADMIN_ENDPOINT}/slots`, undefined, {
    params: { ...scope, ...range, ...paging },
    signal,
  });
}

/** An organization's week of slots, fetched one day per request. */
export async function listWeekAdminSlots(scope: OrgScoped, range: SlotRange, signal?: AbortSignal) {
  const days = await Promise.all(
    localDatesOf(range).map((day) =>
      fetchAllPages<AdminSlot>(
        (page, limit, abort) =>
          listAdminSlots(scope, { fromDate: day, toDate: day }, { page, limit }, abort),
        { ...DAY_FETCH_OPTIONS, signal }
      )
    )
  );
  return days.flat();
}

export function listSlotBookings(
  scope: OrgScoped,
  slotStartAt: string,
  paging: PageParams = {},
  signal?: AbortSignal
) {
  return apiRequest<PagedResponse<SlotBooking>>(
    'get',
    `${ADMIN_ENDPOINT}/slots/bookings`,
    undefined,
    { params: { ...scope, slotStartAt, ...paging }, signal }
  );
}

/** All bookings of a slot, across every page. */
export function listAllSlotBookings(scope: OrgScoped, slotStartAt: string, signal?: AbortSignal) {
  return fetchAllPages<SlotBooking>(
    (page, limit, abort) => listSlotBookings(scope, slotStartAt, { page, limit }, abort),
    { ...SLOT_BOOKINGS_FETCH_OPTIONS, signal }
  );
}

export function addBookingForUser(
  scope: OrgScoped,
  data: { slotStartAt: string; userUuid: string }
) {
  return postPlacement(`${ADMIN_ENDPOINT}/bookings`, { ...scope, ...data });
}

export function forceReleaseBooking(scope: OrgScoped, bookingUuid: string, reason: string) {
  return apiRequest<BookingResult>(
    'post',
    `${ADMIN_ENDPOINT}/bookings/${bookingUuid}/force-release`,
    {
      ...scope,
      reason,
    }
  );
}

export function cancelSlot(scope: OrgScoped, slotStartAt: string, reason: string) {
  return apiRequest<void>('post', `${ADMIN_ENDPOINT}/slots/cancel`, {
    ...scope,
    slotStartAt,
    reason,
  });
}
